import { useEffect, useRef, useState, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  once?: boolean;
  animation?: "fade-up" | "fade-in" | "slide-left" | "slide-right" | "scale" | "fade-down";
}

const ScrollReveal = ({
  children,
  className,
  delay = 0,
  duration = 600,
  once = true,
  animation = "fade-up",
}: ScrollRevealProps) => {
  // Start visible to prevent layout shift, then animate only if element was below fold
  const [isVisible, setIsVisible] = useState(true);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hasCheckedInitial = useRef(false);

  useEffect(() => {
    if (!ref.current || hasCheckedInitial.current) return;
    hasCheckedInitial.current = true;

    // Use requestAnimationFrame to batch layout reads and avoid forced reflow
    const rafId = requestAnimationFrame(() => {
      if (!ref.current) return;
      
      const rect = ref.current.getBoundingClientRect();
      const isInInitialViewport = rect.top < window.innerHeight && rect.bottom > 0;

      if (!isInInitialViewport) {
        // Element is below the fold - set up animation
        setShouldAnimate(true);
        setIsVisible(false);

        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              if (once && ref.current) {
                observer.unobserve(ref.current);
              }
            } else if (!once) {
              setIsVisible(false);
            }
          },
          {
            threshold: 0.1,
            rootMargin: "0px 0px -20px 0px",
          }
        );

        if (ref.current) {
          observer.observe(ref.current);
        }
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [once]);

  const animationClasses = {
    "fade-up": {
      hidden: "opacity-0 translate-y-6",
      visible: "opacity-100 translate-y-0",
    },
    "fade-down": {
      hidden: "opacity-0 -translate-y-6",
      visible: "opacity-100 translate-y-0",
    },
    "fade-in": {
      hidden: "opacity-0",
      visible: "opacity-100",
    },
    "slide-left": {
      hidden: "opacity-0 translate-x-6",
      visible: "opacity-100 translate-x-0",
    },
    "slide-right": {
      hidden: "opacity-0 -translate-x-6",
      visible: "opacity-100 translate-x-0",
    },
    scale: {
      hidden: "opacity-0 scale-95",
      visible: "opacity-100 scale-100",
    },
  };

  // If no animation needed, render children directly with visible styles
  if (!shouldAnimate) {
    return (
      <div ref={ref} className={cn(animationClasses[animation].visible, className)}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        "transition-all",
        isVisible ? animationClasses[animation].visible : animationClasses[animation].hidden,
        className
      )}
      style={{
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`,
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      {children}
    </div>
  );
};

export default ScrollReveal;
